import React, { useState, useEffect } from 'react';
import {
  ShoppingBagIcon,
  UserGroupIcon,
  ChartBarIcon,
  UsersIcon,
} from '@heroicons/react/24/solid';
import { ActivityFeed, Activity } from '../../components/admin/ActivityFeed';
import { api } from '../../services/api';
import { Button } from '../../components/ui/Button';

interface DashboardStats {
  users: {
    total: number;
    admins: number;
  };
  products: {
    total: number;
    active: number;
  };
  racers: {
    total: number;
    active: number;
  };
}

interface ChartData {
  labels: string[];
  data: number[];
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<any>;
  change?: string;
  trend?: 'up' | 'down';
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, change }) => (
  <div className="p-5" style={{ background: '#131313', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' }}>
    <div className="flex items-center gap-4">
      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-sm" style={{ background: 'rgba(217,48,37,0.10)' }}>
        <Icon className="h-5 w-5" style={{ color: '#D93025' }} />
      </div>
      <div>
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '10px', fontWeight: 700, color: '#4A4A3E', textTransform: 'uppercase', letterSpacing: '0.18em', marginBottom: '2px' }}>{title}</p>
        <p style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', letterSpacing: '0.04em', color: '#EEEAD5', lineHeight: 1 }}>{value}</p>
        {change && <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '11px', color: '#888874', marginTop: '2px' }}>{change}</p>}
      </div>
    </div>
  </div>
);

export const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    users: { total: 0, admins: 0 },
    products: { total: 0, active: 0 },
    racers: { total: 0, active: 0 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [activityError, setActivityError] = useState<string | undefined>();


  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await api.admin.getStats();
        setStats(data);
        setError(null);
      } catch (error) {
        console.error('Error fetching stats:', error);
        const message = error instanceof Error ? error.message : 'Failed to load dashboard statistics';
        setError(message);
        if (error instanceof Error && error.message.includes('401')) {
          // Handle unauthorized access
          window.location.href = '/login';
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchActivities = async () => {
      setIsLoadingActivities(true);
      try {
        // TODO: Replace with actual API call once implemented
        await new Promise(resolve => setTimeout(resolve, 1000));
        setActivities([
          {
            id: '1',
            type: 'new_racer',
            title: 'New Racer Registration',
            description: 'New racer joined the platform',
            timestamp: new Date().toISOString(),
            metadata: {
              racerId: 'r123',
              racerName: 'John Doe'
            }
          },
          {
            id: '2',
            type: 'product_purchase',
            title: 'Product Update',
            description: 'Product stock updated',
            timestamp: new Date().toISOString(),
            metadata: {
              productId: 'p456',
              productName: 'Racing Helmet'
            }
          },
        ]);
        setActivityError(undefined);
      } catch (error) {
        setActivityError('Failed to load recent activities');
        console.error('Error fetching activities:', error);
      } finally {
        setIsLoadingActivities(false);
      }
    };

    fetchActivities();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderBottomColor: '#D93025' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-900/50 border border-red-500/20 text-red-300 px-4 py-3 rounded-lg" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      </div>
    );
  }

  const panelStyle = { background: '#0F0F0F', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '4px' };
  const panelHeaderStyle: React.CSSProperties = {
    padding: '14px 20px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '10px',
    fontWeight: 700,
    color: '#4A4A3E',
    textTransform: 'uppercase',
    letterSpacing: '0.2em',
  };

  return (
    <div className="p-6 space-y-5">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Users" value={stats.users.total} icon={UsersIcon} change={`${stats.users.admins} admins`} />
        <StatCard title="Products" value={stats.products.total} icon={ShoppingBagIcon} change={`${stats.products.active} active`} />
        <StatCard title="Racers" value={stats.racers.total} icon={UserGroupIcon} change={`${stats.racers.active} active`} />
      </div>

      {/* Recent Activity */}
      <div style={panelStyle}>
        <div style={panelHeaderStyle}>Recent Activity</div>
        <div className="p-5">
          <ActivityFeed activities={activities} isLoading={isLoadingActivities} error={activityError} />
        </div>
      </div>
    </div>
  );
}; 